import {
    useState,
    useRef,
    useEffect,
    useContext,
    useCallback,
    useMemo,
} from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './quiz-text-field.module.scss';
import { QuizContext } from 'src/views/Quiz/Quiz.context';
import useAutosizeTextArea from 'src/hooks/useAutosizeTextArea';
import QuizFollowUpField from '../QuizFollowUpField/QuizFollowUpField';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { QUIZ_EVENT_TYPES } from 'src/types/Quiz';

const QUIZ_TEXT_FIELD_DEFAULT_HEIGHT = 113;

const QuizTextField = () => {
    const {
        currentQuestion,
        handleSubmitAnswer,
        questionById,
        prefillQuestionIds,
        submittedQuestionIds,
        inQueue,
        state,
    } = useContext(QuizContext);
    const {
        questionId,
        questionText,
        questionExplanation,
        answerList,
        userAnswers,
        prefillAnswers,
        prefillNeeded,
        medication,
        parentQuestion: parentQuestionId,
    } = currentQuestion || {};
    const { frontendStamp: answerId } = answerList[0] || {};
    const parentQuestion = useMemo(
        () => (parentQuestionId ? questionById[parentQuestionId] : null),
        [parentQuestionId, questionById],
    );
    const [pageTransitioning] = state.pageTransitioning;
    const [continueDisabled, setContinueDisabled] = state.continueDisabled;
    const [textAreaValue, setTextAreaValue] = useState<string>('');
    const [isError, setIsError] = useState(false);
    const [inputFocussed, setInputFocussed] = useState(false);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    useAutosizeTextArea(
        textAreaRef.current,
        textAreaValue,
        QUIZ_TEXT_FIELD_DEFAULT_HEIGHT,
    );

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const textValue = event.target?.value;
        setTextAreaValue(textValue);
    };

    // Set existing answer
    useEffect(() => {
        let newAnswerText: string | null = null;
        if (
            questionId &&
            submittedQuestionIds.includes(questionId) &&
            userAnswers.length > 0
        ) {
            newAnswerText = userAnswers[0].answerText;
        } else if (
            inQueue &&
            prefillNeeded &&
            prefillQuestionIds.includes(questionId) &&
            prefillAnswers.length > 0
        ) {
            newAnswerText = prefillAnswers[0].answerText;
        }
        if (newAnswerText) {
            setTextAreaValue(newAnswerText);
        }
    }, [
        questionId,
        userAnswers,
        prefillAnswers,
        prefillNeeded,
        prefillQuestionIds,
        submittedQuestionIds,
        inQueue,
    ]);
    // Handle submitting
    const handleSubmit = useCallback(() => {
        if (textAreaValue.length > 0 && !continueDisabled && questionId) {
            setIsError(false);
            handleSubmitAnswer({
                questionId: questionId,
                questionText: questionText,
                answerIds: answerId ? [answerId] : undefined,
                answerTexts: [textAreaValue],
                medication: medication ? medication : '',
            });
        } else {
            setIsError(true);
        }
    }, [
        continueDisabled,
        textAreaValue,
        questionId,
        questionText,
        answerId,
        medication,
        handleSubmitAnswer,
    ]);
    // Continue click listener
    useEffect(() => {
        window.addEventListener(QUIZ_EVENT_TYPES.continueClick, handleSubmit);

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.continueClick,
                handleSubmit,
            );
    }, [handleSubmit]);
    // Enter key press listener
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter' && !inputFocussed) handleSubmit();
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () =>
            window.removeEventListener('keydown', handleKeyPress, false);
    }, [handleSubmit, inputFocussed]);
    // Continue state
    useEffect(() => {
        if (!pageTransitioning) {
            setContinueDisabled(
                textAreaValue === null || textAreaValue.length === 0,
            );
        }
    }, [textAreaValue, pageTransitioning, setContinueDisabled]);

    return (
        <>
            {parentQuestion ? <QuizFollowUpField {...parentQuestion} /> : null}
            <div className={styles['quiz-text-field--wrapper']}>
                {/* Content header */}
                <div className={styles['quiz-field-header']}>
                    {/* Title */}
                    <h4 className={styles['title']}>{questionText}</h4>
                    {/* Subtitle */}
                    {questionExplanation ? (
                        <h5 className={styles['subtitle']}>
                            {questionExplanation}
                        </h5>
                    ) : null}
                </div>
                <div className={styles['textarea--wrapper']}>
                    <textarea
                        className={classNames(
                            styles['default-field'],
                            isError ? styles['error-field'] : '',
                        )}
                        rows={3}
                        id={questionId}
                        data-testid="free-text-input"
                        ref={textAreaRef}
                        value={textAreaValue || ''}
                        onChange={handleChange}
                        required
                        autoFocus
                        onFocus={() => setInputFocussed(true)}
                        onBlur={() => setInputFocussed(false)}
                    />
                    {isError && (
                        <p className={classNames(styles['error-message'])}>
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                            This field is required
                        </p>
                    )}
                </div>
            </div>
        </>
    );
};

export default QuizTextField;
